import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { InView } from 'react-intersection-observer';
import { take as lodashTake } from 'global/lib/lodash';
import { CenterLoader } from 'global/styles';
import { FlexGridList, FlexGridRow } from './index';
import { renderFlexGridColumns, renderFlexGridHeaderCells, resolveColumns } from './helpers';
import { FlexGridSortableProvider, FlexGridSortableRow } from './sortable';

/**
 * Column-config table on FlexGridList — drop-in for DndTable (sortable optional).
 *
 * Column extras:
 * - `sortable` + `sortDir` + `onSort` → FlexGridSortHeader in the header cell
 * - `noGrow` → skip auto flexGrow on this column
 * - first non-action column gets `flexGrow: 1` when none set
 *
 * @param {function} [renderRow] - custom body; use renderFlexGridColumns(columns, item) inside
 * @param {function} [getRowProps] - (item, index) => props (e.g. { dimmed: true })
 */
export function FlexGridDataTable({
  columns: columnsIn = [],
  items = [],
  onSelectRow,
  take: takeInit = 10,
  takeAmt = 10,
  // Off by default — most admin lists load fully. Opt in for true windowed/infinite lists.
  paginate = false,
  showHeader = true,
  dense = true,
  sortable = false,
  canDrag = true,
  onOrderChange,
  renderRow,
  getRowProps,
  rowMinHeight = 48,
  sx,
}) {
  const columns = resolveColumns(columnsIn);
  const [takeCount, setTakeCount] = useState(takeInit);
  const [dragActive, setDragActive] = useState(false);
  const [localItems, setLocalItems] = useState(items || []);

  useEffect(() => {
    setTakeCount(takeInit);
  }, [items, takeInit]);

  useEffect(() => {
    setLocalItems(items || []);
  }, [items]);

  const sourceItems = sortable ? localItems : items || [];
  const visible = paginate ? lodashTake(sourceItems, takeCount) : sourceItems;
  const ids = visible.map((i) => i.id);
  const usePropZebra = sortable && (canDrag || dragActive);
  const zebraMode = usePropZebra ? 'prop' : 'css';

  const handleOrderChange = (next) => {
    setLocalItems(next);
    onOrderChange && onOrderChange(next);
  };

  const rowMinSx = rowMinHeight != null ? { minHeight: rowMinHeight, boxSizing: 'border-box' } : null;

  const header = showHeader ? (
    <FlexGridRow header layout="columns" dense={dense} sx={rowMinSx}>
      {renderFlexGridHeaderCells(columns)}
    </FlexGridRow>
  ) : null;

  const rows = visible.map((item, index) => {
    const striped = usePropZebra && !dragActive && index % 2 === 1;
    const extraRowProps = (typeof getRowProps === 'function' && getRowProps(item, index)) || {};
    const mergedSx = { ...rowMinSx, ...(extraRowProps.sx || {}) };
    const body = renderRow ? renderRow(item, { columns, index }) : renderFlexGridColumns(columns, item, index);

    if (sortable) {
      return (
        <FlexGridSortableRow
          key={item.id}
          id={item.id}
          canDrag={canDrag}
          layout={renderRow ? 'block' : 'columns'}
          dense={dense}
          striped={striped}
          clickable={!!onSelectRow && !canDrag}
          hover={!!onSelectRow && !canDrag}
          onClick={onSelectRow && !canDrag ? (e) => onSelectRow(item, e) : undefined}
          {...extraRowProps}
          sx={mergedSx}
        >
          {body}
        </FlexGridSortableRow>
      );
    }

    return (
      <FlexGridRow
        key={item.id}
        layout={renderRow ? 'block' : 'columns'}
        dense={dense}
        clickable={!!onSelectRow}
        hover={!!onSelectRow}
        onClick={onSelectRow ? (e) => onSelectRow(item, e) : undefined}
        {...extraRowProps}
        sx={mergedSx}
      >
        {body}
      </FlexGridRow>
    );
  });

  const list = (
    <FlexGridList zebra zebraMode={zebraMode}>
      {rows}
    </FlexGridList>
  );

  return (
    <Box
      sx={{
        width: '100%',
        '& .parentIcon': {
          transform: 'rotate(90deg)',
          fontSize: 10,
          marginTop: '-8px',
          marginRight: 0.5,
        },
        ...sx,
      }}
    >
      {header}
      {sortable ? (
        <FlexGridSortableProvider
          ids={ids}
          items={visible}
          onOrderChange={(nextVisible) => {
            if (localItems.length <= visible.length) {
              handleOrderChange(nextVisible);
              return;
            }
            const headIds = new Set(visible.map((i) => i.id));
            const tail = localItems.filter((i) => !headIds.has(i.id));
            handleOrderChange([...nextVisible, ...tail]);
          }}
          onDragActiveChange={setDragActive}
        >
          {list}
        </FlexGridSortableProvider>
      ) : (
        list
      )}
      {paginate && sourceItems.length > takeCount && (
        <InView
          onChange={(inView) => {
            if (inView) setTakeCount((t) => t + takeAmt);
          }}
        >
          <CenterLoader style={{ overflow: 'hidden' }} />
        </InView>
      )}
    </Box>
  );
}

export default FlexGridDataTable;
